import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Turnstile } from '@marsidev/react-turnstile';
import imgLogo from '../../assets/img/logo-login.svg';
import sesacoLogo from '../../assets/img/sesaco-logo.jpeg';
import backgroundImg from '../../assets/img/bg_login.jpeg';
import { useLoginMutation } from '../../services/auth.service';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import useModalConfirm from '../../hooks/useModalConfirm';

const RootBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
}));

const TextRight = styled(Box)(({ theme }) => ({
  padding: '32px',
  textAlign: 'center',
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '16px',
  background: '#D9042F',
  position: 'absolute',
  bottom: '0',
  right: '0',
  width: '100%',
  boxSizing: 'border-box',
  textTransform: 'uppercase',
}));

const TextBottomPage = styled(Box)({
  color: '#BDBDBD',
  textAlign: 'center',
  width: '100%',
  marginTop: '7vh',
  fontSize: '16px',
});

const LoginButton = styled(Button)({
  marginBottom: '16px',
  padding: '10px 20px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  fontSize: '18px',
  fontWeight: 500,
  backgroundColor: '#D9042F',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#b80327',
  },
  '&:disabled': {
    backgroundColor: '#D9042F80',
    color: '#fff',
  },
});

const RedText = styled(Typography)({
  color: '#af4c0f',
});

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#666',
    fontSize: '16px',
  },
  '&:hover .MuiInputLabel-root': {
    color: '#D9042F',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: '#666',
  },
  '&:hover .MuiInput-underline:before': {
    borderBottomColor: '#D9042F',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#D9042F',
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Roboto',
    fontSize: '16px',
    backgroundColor: '#e6f0ff',
  },
  '& .MuiFormHelperText-root': {
    fontSize: '12px',
  },
}));

const CustomCheckbox = styled(Checkbox)({
  '&.MuiCheckbox-root': {
    color: '#666',
  },
  '&.Mui-checked': {
    color: '#D9042F',
  },
});

const CaptchaContainer = styled(Box)({
  marginTop: '16px',
  display: 'flex',
});

const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showModalConfirm, hideModalConfirm } = useModalConfirm();
  const [loginUser, { isLoading }] = useLoginMutation();
  const { setSnackbar } = useSnackbar();
  const [token] = useLocalStorage('access_token', '');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const turnstileRef = useRef<any>(null);

  const from = location.state?.from?.pathname || '/control';

  useEffect(() => {
    if (token) {
      navigate(from);
    }
  }, [token, navigate, from]);

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const resetTurnstile = useCallback(() => {
    if (turnstileRef.current) {
      turnstileRef.current.reset();
    }
    setTurnstileToken('');
  }, []);

  const handleForgotPass = () => {
    showModalConfirm({
      title: 'Quên mật khẩu',
      content: 'Vui lòng liên hệ Admin để được cấp lại mật khẩu của tài khoản',
      confirm: {
        action: hideModalConfirm,
      },
    });
  };

  const handleCheckRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  const isStillLocked = (lockedUntil: number | null) => {
    if (!lockedUntil) return false;
    return lockedUntil * 1000 > Date.now();
  };

  useEffect(() => {
  if (!lockedUntil) return;

  const now = Date.now();
  const timeoutMs = lockedUntil * 1000 - now;
  if (timeoutMs <= 0) {
    setIsAccountLocked(false);
    setLockedUntil(null);
    return;
  }

  const timeout = setTimeout(() => {
    setIsAccountLocked(false);
    setLockedUntil(null);
  }, timeoutMs);

  return () => clearTimeout(timeout);
}, [lockedUntil]);

  return (
    <RootBox title="Đăng nhập">
      <Grid container sx={{ height: '100%' }}>
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
            <Container maxWidth="sm" sx={{ mt: '50px' }}>
              <Formik
                initialValues={{
                  username: '',
                  password: '',
                }}
                validationSchema={Yup.object().shape({
                  username: Yup.string()
                    .trim()
                    .min(6, 'Tên đăng nhập tối thiểu 6 kí tự')
                    .required('Tên đăng nhập không được để trống'),
                  password: Yup.string()
                    .trim()
                    .min(6, 'Mật khẩu tối thiểu 6 kí tự')
                    .required('Mật khẩu không được để trống'),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    if (!turnstileToken) {
                      setSubmitting(false);
                      return;
                    }
                    await loginUser({
                      ...values,
                      siteverify: turnstileToken,
                    }).unwrap();
                    setIsAccountLocked(false);
                    setLockedUntil(null);
                    navigate(from);
                  } catch (error: any) {
                    let message = error.data?.message || 'Sai tài khoản hoặc mật khẩu';
                    if (error.data?.error === 'user is locked') {
                      setIsAccountLocked(true);
                      setLockedUntil(error.data.lockedUntil);
                      message = `Tài khoản bị 10 phút do sai mật khẩu quá 3 lần `;
                    }
                    setSnackbar({ open: true, message, severity: 'error' });
                    resetTurnstile();
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                  <form onSubmit={handleSubmit}>
                    <Box mb={1} sx={{ textAlign: 'left' }}>
                      <Box
                        sx={{
                          width: '90px',
                          height: '98px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          alt="Logo"
                          src={sesacoLogo}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      <RedText sx={{ fontSize: '30px', mt: '32px', fontWeight: 700 }} variant="h2">
                        Đăng nhập
                      </RedText>
                    </Box>
                    <CustomTextField
                      variant="standard"
                      error={Boolean(touched.username && errors.username)}
                      fullWidth
                      helperText={touched.username && errors.username}
                      label="Tên đăng nhập"
                      margin="normal"
                      name="username"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        resetTurnstile();
                        setIsAccountLocked(false);
                        setLockedUntil(null);
                      }}
                      type="text"
                      value={values.username}
                    />
                    <CustomTextField
                      variant="standard"
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label="Mật khẩu"
                      margin="normal"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                              {showPassword ? (
                                <VisibilityOff sx={{ color: '#D9042F' }} />
                              ) : (
                                <Visibility sx={{ color: '#D9042F' }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {isAccountLocked && lockedUntil && isStillLocked(lockedUntil) && (
                      <RedText sx={{ mt: 2, fontSize: '14px', fontWeight: 500 }}>
                        Tài khoản bị khóa 10 phút đến {new Date(lockedUntil * 1000).toLocaleString('vi-VN', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}. Vui lòng thử lại sau
                      </RedText>
                    )}
                    <CaptchaContainer>
                      <Turnstile
                        ref={turnstileRef}
                        siteKey="0x4AAAAAABhlqvV15C9FE2SO"
                        onSuccess={handleTurnstileSuccess}
                        options={{ size: 'normal', theme: 'auto', action: 'login' }}
                      />
                    </CaptchaContainer>
                    <Box my={2}>
                      <Grid container>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <CustomCheckbox
                                checked={rememberMe}
                                onChange={handleCheckRememberMe}
                                name="rememberMe"
                              />
                            }
                            label={<span style={{ fontFamily: 'Roboto', fontSize: '16px' }}>Nhớ đăng nhập</span>}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#D9042F',
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              fontFamily: 'Roboto',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#D9042F',
                              fontWeight: 700,
                              fontSize: '18px',
                            }}
                            onClick={handleForgotPass}
                          >
                            Quên mật khẩu?
                          </button>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box my={2}>
                      <LoginButton
                        disabled={isLoading || isSubmitting || !turnstileToken || (isAccountLocked && isStillLocked(lockedUntil))}
                        fullWidth
                        size="large"
                        type="submit"
                      >
                        Đăng nhập
                        {isLoading && (
                          <Box sx={{ ml: 2 }}>
                            <CircularProgress sx={{ color: '#B30326' }} size={20} />
                          </Box>
                        )}
                      </LoginButton>
                      <Typography
                        component="span"
                        sx={{
                          color: '#BDBDBD',
                          textDecoration: 'underline',
                          mt: '12px',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                        onClick={() => navigate('/privacy-policy')}
                      >
                        <i>Chính sách quyền riêng tư</i>
                      </Typography>
                    </Box>
                    <TextBottomPage>
                      {/* <span>
                        {window.location.host === 'sesaco.vncss.net'
                          ? `Sản phẩm thuộc bản quyền công ty Sesaco Việt Nam. Hotline: 02432262556.`
                          : `Sản phẩm thuộc bản quyền công ty `}
                        {window.location.host !== 'sesaco.vncss.net' && (
                          <a target="_blank" href="https://vntech24h.com/" style={{ textDecoration: 'none' }}>
                            Vntech24h.,Jsc.
                          </a>
                        )}
                        {window.location.host !== 'sesaco.vncss.net' && ` Hotline 0934.88.83.86 hoặc 0706.90.3333`}
                      </span> */}
                    </TextBottomPage>
                  </form>
                )}
              </Formik>
            </Container>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ m: 0, p: 0, position: 'relative', height: '100vh' }}>
          <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
            <Box sx={{ position: 'relative', height: '100vh' }}>
              <img alt="Background" src={backgroundImg} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <TextRight>
                <span>
                  {window.location.host === 'sesaco.vncss.net'
                    ? 'sesaco hệ thống an ninh tập trung'
                    : 'vncss hệ thống an ninh tập trung'}
                </span>
              </TextRight>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </RootBox>
  );
};

export default LoginScreen;
